'use client';

import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import { Dialog, DialogBackdrop, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import { twMerge } from 'tailwind-merge';
import cx from 'classnames';
import { ButtonIcon } from '@components/button-icon';
import { IconNames } from '@components/icon';
import { zIndex } from '@utils/constants';
export type BaseModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';
type BaseModalZIndex = 'modal' | 'fullScreenModal';
export interface BaseModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal should close (escape key, backdrop click, close button) */
  onClose: () => void;
  /** Modal content */
  children: React.ReactNode;
  /** Panel max-width size. Default: 'md' */
  size?: BaseModalSize;
  /** Z-index level from constants. Default: 'modal' */
  zIndexLevel?: BaseModalZIndex;
  /** Additional classes for the DialogPanel */
  className?: string;
  /** Override backdrop classes. Default: 'bg-primary-main/60' */
  backdropClassName?: string;
  /** Whether clicking backdrop closes the modal. Default: true */
  closeOnBackdrop?: boolean;
  /** Whether to show the built-in close (X) button. Default: true */
  showCloseButton?: boolean;
  /** Accessible label for the dialog */
  ariaLabel?: string;
}
const SIZE_CLASSES: Record<BaseModalSize, string> = {
  sm: 'max-w-md',
  md: 'max-w-xl',
  lg: 'max-w-4xl',
  xl: 'max-w-6xl lg:w-3/4',
  full: 'fixed inset-0'
};
const Z_INDEX_MAP: Record<BaseModalZIndex, number> = {
  modal: zIndex.modal,
  fullScreenModal: zIndex.fullScreenModal
};
const noop = () => {};
export const BaseModal: React.FC<BaseModalProps> = t0 => {
  const $ = _c(50);
  const {
    isOpen,
    onClose,
    children,
    size: t1,
    zIndexLevel: t2,
    className,
    backdropClassName,
    closeOnBackdrop: t3,
    showCloseButton: t4,
    ariaLabel
  } = t0;
  const size = t1 === undefined ? "md" : t1;
  const zIndexLevel = t2 === undefined ? "modal" : t2;
  const closeOnBackdrop = t3 === undefined ? true : t3;
  const showCloseButton = t4 === undefined ? true : t4;
  const isFullScreen = size === "full";
  let t5;
  let t6;
  if ($[0] !== isOpen) {
    t5 = () => {
      if (isOpen) {
        document.body.classList.add("modalActive");
      } else {
        document.body.classList.remove("modalActive");
      }
      return _temp;
    };
    t6 = [isOpen];
    $[0] = isOpen;
    $[1] = t5;
    $[2] = t6;
  } else {
    t5 = $[1];
    t6 = $[2];
  }
  React.useEffect(t5, t6);
  const t7 = SIZE_CLASSES[size];
  const t8 = !isFullScreen && "rounded-2xl";
  const t9 = !isFullScreen && "sm:my-8 sm:w-full";
  const t10 = !isFullScreen && "border-[1.5px] border-primary-main";
  let t11;
  if ($[3] !== className || $[4] !== t10 || $[5] !== t7 || $[6] !== t8 || $[7] !== t9) {
    t11 = twMerge("relative w-full transform overflow-hidden text-left shadow-xl transition-all", t7, t8, "bg-white", t9, t10, className);
    $[3] = className;
    $[4] = t10;
    $[5] = t7;
    $[6] = t8;
    $[7] = t9;
    $[8] = t11;
  } else {
    t11 = $[8];
  }
  const panelClasses = t11;
  const t12 = backdropClassName || "bg-primary-main/60";
  let t13;
  if ($[9] !== t12) {
    t13 = twMerge("fixed inset-0 transition-opacity", t12);
    $[9] = t12;
    $[10] = t13;
  } else {
    t13 = $[10];
  }
  const backdropClasses = t13;
  const t14 = closeOnBackdrop ? onClose : noop;
  const t15 = Z_INDEX_MAP[zIndexLevel];
  let t16;
  if ($[11] !== t15) {
    t16 = {
      zIndex: t15
    };
    $[11] = t15;
    $[12] = t16;
  } else {
    t16 = $[12];
  }
  let t17;
  if ($[13] !== backdropClasses) {
    t17 = <TransitionChild enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0"><DialogBackdrop className={backdropClasses} /></TransitionChild>;
    $[13] = backdropClasses;
    $[14] = t17;
  } else {
    t17 = $[14];
  }
  const t18 = isFullScreen ? "flex" : "flex items-center justify-center p-4 sm:p-0";
  let t19;
  if ($[15] !== t18) {
    t19 = cx("min-h-full text-center", t18);
    $[15] = t18;
    $[16] = t19;
  } else {
    t19 = $[16];
  }
  const t20 = isFullScreen ? "translate-y-4" : "translate-y-4 sm:translate-y-0 sm:scale-95";
  let t21;
  if ($[17] !== t20) {
    t21 = cx("opacity-0", t20);
    $[17] = t20;
    $[18] = t21;
  } else {
    t21 = $[18];
  }
  const t22 = isFullScreen ? "translate-y-0" : "translate-y-0 sm:scale-100";
  let t23;
  if ($[19] !== t22) {
    t23 = cx("opacity-100", t22);
    $[19] = t22;
    $[20] = t23;
  } else {
    t23 = $[20];
  }
  const t24 = isFullScreen ? "translate-y-0" : "translate-y-0 sm:scale-100";
  let t25;
  if ($[21] !== t24) {
    t25 = cx("opacity-100", t24);
    $[21] = t24;
    $[22] = t25;
  } else {
    t25 = $[22];
  }
  const t26 = isFullScreen ? "translate-y-4" : "translate-y-4 sm:translate-y-0 sm:scale-95";
  let t27;
  if ($[23] !== t26) {
    t27 = cx("opacity-0", t26);
    $[23] = t26;
    $[24] = t27;
  } else {
    t27 = $[24];
  }
  let t28;
  if ($[25] !== onClose || $[26] !== showCloseButton) {
    t28 = showCloseButton && <div className="absolute top-3 right-3 z-10"><ButtonIcon type="button" onClick={onClose} variant="text" size="sm" color="gray" icon={IconNames.X} ariaLabel="Close modal" isCircle={true} /></div>;
    $[25] = onClose;
    $[26] = showCloseButton;
    $[27] = t28;
  } else {
    t28 = $[27];
  }
  let t29;
  if ($[28] !== children || $[29] !== panelClasses || $[30] !== t28) {
    t29 = <DialogPanel className={panelClasses}>{t28}{children}</DialogPanel>;
    $[28] = children;
    $[29] = panelClasses;
    $[30] = t28;
    $[31] = t29;
  } else {
    t29 = $[31];
  }
  let t30;
  if ($[32] !== t21 || $[33] !== t23 || $[34] !== t25 || $[35] !== t27 || $[36] !== t29) {
    t30 = <TransitionChild enter="ease-out duration-300" enterFrom={t21} enterTo={t23} leave="ease-in duration-200" leaveFrom={t25} leaveTo={t27}>{t29}</TransitionChild>;
    $[32] = t21;
    $[33] = t23;
    $[34] = t25;
    $[35] = t27;
    $[36] = t29;
    $[37] = t30;
  } else {
    t30 = $[37];
  }
  let t31;
  if ($[38] !== t19 || $[39] !== t30) {
    t31 = <div className="fixed inset-0 overflow-y-auto"><div className={t19}>{t30}</div></div>;
    $[38] = t19;
    $[39] = t30;
    $[40] = t31;
  } else {
    t31 = $[40];
  }
  let t32;
  if ($[41] !== ariaLabel || $[42] !== t14 || $[43] !== t16 || $[44] !== t17 || $[45] !== t31) {
    t32 = <Dialog onClose={t14} className="relative" style={t16} aria-label={ariaLabel}>{t17}{t31}</Dialog>;
    $[41] = ariaLabel;
    $[42] = t14;
    $[43] = t16;
    $[44] = t17;
    $[45] = t31;
    $[46] = t32;
  } else {
    t32 = $[46];
  }
  let t33;
  if ($[47] !== isOpen || $[48] !== t32) {
    t33 = <Transition show={isOpen} appear={true}>{t32}</Transition>;
    $[47] = isOpen;
    $[48] = t32;
    $[49] = t33;
  } else {
    t33 = $[49];
  }
  return t33;
};
BaseModal.displayName = 'BaseModal';
function _temp() {
  document.body.classList.remove("modalActive");
}